import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { CreateAppendMessage } from '@assistant-ui/react';
import { ThreadMessageLike } from '@assistant-ui/react';
import { useLocalRuntime } from '@assistant-ui/react';
import { ThreadAssistantMessagePart } from '@assistant-ui/react';
import { useChatEventBridge } from 'apps/sigma2-ui/src/lib/hooks/useChatEventBridge';
import { ChatEvent } from 'libs/end-to-end/src/chat/chat-events';
import { AsyncQueue } from 'libs/end-to-end/src/async-queue';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { ChatMessageContentPart } from 'libs/end-to-end/src/chat/chats';
import { StartCustomVizArgs } from 'libs/end-to-end/src/chat/chats';
import { CreateChatRequest } from 'libs/end-to-end/src/chat/chats';
import { CreateChatResponse } from 'libs/end-to-end/src/chat/chats';
import { GetChatResponse } from 'libs/end-to-end/src/chat/chats';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { z } from 'zod';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { CustomViz } from 'apps/sigma2-ui/src/components/custom-viz/CustomViz';
import { lookupCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/CustomVizDefinitions';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { parseCustomVizResult } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { isErr } from 'libs/end-to-end/src/result';
import { isOk } from 'libs/end-to-end/src/result';
import { isShowChatbotEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isEntitledToAi } from 'libs/end-to-end/src/group-entitlements';

type JsonValue =
  | string
  | number
  | boolean
  | null
  | { [key: string]: JsonValue }
  | JsonValue[];

type JsonObject = { [key: string]: JsonValue };

const JsonObjectSchema: z.ZodType<JsonObject> = z.lazy(() =>
  z.record(
    z.union([
      z.string(),
      z.number(),
      z.boolean(),
      z.null(),
      z.array(JsonObjectSchema),
      JsonObjectSchema,
    ]),
  ),
);

// eslint-disable-next-line complexity
const toJsonValue = (value: unknown): JsonValue => {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(toJsonValue);
  }

  if (typeof value === 'object' && value !== null) {
    const result: Record<string, JsonValue> = {};

    for (const [k, v] of Object.entries(value)) {
      result[k] = toJsonValue(v);
    }

    return result;
  }

  if (value === undefined) {
    return null;
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  if (typeof value === 'symbol' || typeof value === 'function') {
    return null;
  }

  return null;
};

const toJsonObject = (value: unknown): JsonObject => {
  const json = toJsonValue(value);

  if (typeof json === 'object' && json !== null && !Array.isArray(json)) {
    return json;
  }

  return {};
};

const mapDbMessageToThreadMessage = (message: {
  role: 'user' | 'assistant' | 'system';
  parts: ChatMessageContentPart[];
}): ThreadMessageLike => {
  return {
    role: message.role === 'system' ? 'assistant' : message.role,
    content: message.parts.map((p) => {
      if (p.type === 'text') {
        return {
          type: 'text',
          text: p.text,
        };
      }

      if (p.type === 'tool-call') {
        return {
          type: 'tool-call',
          toolCallId: p.toolCallId,
          toolName: p.name,
          args: toJsonObject(p.args), // ✅ satisfies ReadonlyJSONObject
          argsText: JSON.stringify(p.args),
        };
      }

      // tool-result
      return {
        type: 'data',
        name: p.name ?? 'tool-result',
        data: {
          toolCallId: p.toolCallId,
          result: toJsonValue(p.result),
        },
      };
    }),
  };
};

const createCustomVizResultMessage = (payload: {
  toolCallId: string;
  status: 'success' | 'error';
  result?: unknown;
  error?: unknown;
}): CreateAppendMessage => ({
  role: 'user',
  content: [
    {
      type: 'data',
      name: 'custom_viz_result',
      data: payload,
    },
  ],
});

const AssistantProviderInner = ({
  organizationId,
  databaseId,
  databaseRevisionId,
  children,
}: {
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  children: React.ReactNode;
}) => {
  const { subscribeToChat } = useChatEventBridge();
  const { userAuthenticatedApiCall } = useUser();

  const [conversationId, setConversationId] = useState<string | null>(() =>
    localStorage.getItem('chat:conversationId'),
  );

  type VizItem = {
    toolCallId: string;
    request: CustomVizRequest;
  };

  const [vizList, setVizList] = useState<VizItem[]>([]);
  const executedVizRef = useRef<Set<string>>(new Set());
  const vizCompletedRef = useRef<Set<string>>(new Set());

  const runtime = useLocalRuntime({
    // eslint-disable-next-line complexity
    async *run({ messages, abortSignal }) {
      let activeConversationId = conversationId;

      const lastMessage = messages[messages.length - 1];

      if (!lastMessage && !activeConversationId) {
        return;
      }

      if (!lastMessage || lastMessage.role !== 'user') {
        return;
      }

      // SEND MESSAGE
      const isCustomVizResultData = (
        data: unknown,
      ): data is {
        toolCallId: string;
        status: 'success' | 'error';
        result?: unknown;
        error?: unknown;
      } => {
        if (typeof data !== 'object' || data === null) return false;

        const d = data as Record<string, unknown>;

        return (
          typeof d.toolCallId === 'string' &&
          (d.status === 'success' || d.status === 'error')
        );
      };

      const toUserChatParts = (
        content: NonNullable<typeof lastMessage>['content'],
      ): ChatMessageContentPart[] => {
        const parts: ChatMessageContentPart[] = [];

        for (const c of content ?? []) {
          switch (c.type) {
            case 'text': {
              parts.push({
                type: 'text',
                text: c.text,
              });
              break;
            }

            case 'data': {
              if (
                c.name === 'custom_viz_result' &&
                isCustomVizResultData(c.data)
              ) {
                parts.push({
                  type: 'tool-result',
                  toolCallId: c.data.toolCallId,
                  name: c.name,
                  result: {
                    status: c.data.status,
                    result: c.data.result,
                    error: c.data.error,
                  },
                });
              }
              break;
            }

            case 'file':
            case 'image':
            case 'audio':
              break;

            default:
              break;
          }
        }

        return parts;
      };

      const parts = toUserChatParts(lastMessage.content);
      if (parts.length === 0) {
        return;
      }

      const payload: CreateChatRequest = {
        conversationId: activeConversationId ?? undefined,
        message: {
          role: 'user',
          parts: parts,
        },
      };

      const res = await userAuthenticatedApiCall({
        responseSchema: CreateChatResponse,
        url: `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/revisions/${databaseRevisionId}/chats`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(payload),
          signal: abortSignal,
        },
      });

      if (abortSignal?.aborted) {
        return;
      }

      if (!res || res.resultType === 'err') {
        console.error('Failed to create chat', res);
        return;
      }

      activeConversationId = res.value.conversationId;

      // only set if first time
      if (!conversationId) {
        setConversationId(activeConversationId);
        localStorage.setItem('chat:conversationId', activeConversationId);
      }

      if (!activeConversationId) return;

      const queue = new AsyncQueue<ChatEvent>();
      let done = false;

      // ✅ IMPORTANT: persistent per message
      let currentParts: ThreadAssistantMessagePart[] = [];

      let unsubscribed = false;

      const unsubscribe = subscribeToChat(activeConversationId, (event) => {
        if (!unsubscribed) {
          queue.push(event);
        }
      });

      const cleanup = () => {
        if (!unsubscribed) {
          unsubscribed = true;
          unsubscribe();
        }
      };

      abortSignal?.addEventListener('abort', cleanup);

      try {
        while (!done) {
          if (abortSignal?.aborted) break;

          const event = await queue.shift();

          switch (event.type) {
            case 'message_start': {
              // ✅ reset ONLY when a new assistant message starts
              currentParts = [];
              break;
            }

            case 'text_delta': {
              const last = currentParts[currentParts.length - 1];

              if (last?.type === 'text') {
                currentParts = [
                  ...currentParts.slice(0, -1),
                  { ...last, text: last.text + event.delta },
                ];
              } else {
                currentParts = [
                  ...currentParts,
                  { type: 'text', text: event.delta },
                ];
              }

              yield { content: currentParts };
              break;
            }

            case 'tool_call': {
              console.log('Tool call event received in runtime', event);
              if (event.toolName !== 'startCustomViz') {
                break;
              }
              const parsedArgs = StartCustomVizArgs.safeParse(event.args);

              if (!parsedArgs.success) {
                console.error(
                  'Invalid tool args',
                  parsedArgs.error,
                  event.args,
                );
                return;
              }

              const args = parsedArgs.data;

              currentParts = [
                ...currentParts,
                {
                  type: 'tool-call',
                  toolName: event.toolName,
                  toolCallId: event.toolCallId,
                  args,
                  argsText: JSON.stringify(args, null, 2),
                },
              ];

              yield { content: currentParts };
              break;
            }

            case 'tool_result': {
              console.log('Tool result event received in runtime', event);
              if (event.toolName === 'startCustomViz') {
                const vizRequest = CustomVizRequest.safeParse(event.result);
                if (!vizRequest.success) {
                  console.error(
                    'Invalid CustomViz result',
                    vizRequest.error,
                    event.result,
                  );
                  break;
                }

                setVizList((prev) => {
                  if (executedVizRef.current.has(event.toolCallId)) return prev;

                  executedVizRef.current.add(event.toolCallId);
                  return [
                    ...prev,
                    {
                      toolCallId: event.toolCallId,
                      request: vizRequest.data,
                    },
                  ];
                });
              }

              currentParts = [
                ...currentParts,
                {
                  type: 'data',
                  name: event.toolName ?? 'tool-result',
                  data: {
                    toolCallId: event.toolCallId,
                    result: event.result,
                  },
                },
              ];

              yield { content: currentParts };
              break;
            }

            case 'error':
              throw new Error(event.message);

            case 'done':
              done = true;
              break;

            case 'message_complete':
              // noop (signal only)
              break;

            default:
              throw guardExhaustive(event);
          }
        }
      } finally {
        unsubscribe();
      }
    },
  });

  const hasHydratedRef = useRef(false);

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isAlive) => {
      if (!isAlive()) return;
      if (!conversationId) return;
      if (hasHydratedRef.current) return;
      hasHydratedRef.current = true;

      const res = await userAuthenticatedApiCall({
        responseSchema: GetChatResponse,
        url: `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/revisions/${databaseRevisionId}/chats/chat/${conversationId}`,
        requestOptions: { method: 'GET' },
      });

      if (!isAlive()) return;

      if (!res || res.resultType === 'err') {
        localStorage.removeItem('chat:conversationId');
        setConversationId(null);
        runtime.thread.reset([]);
        executedVizRef.current.clear();
        vizCompletedRef.current.clear();
        setVizList([]);
        hasHydratedRef.current = false;
        runtime.thread.reset([]);
        return;
      }

      if (res.value.messages.length === 0) {
        localStorage.removeItem('chat:conversationId');
        setConversationId(null);
        runtime.thread.reset([]);
        hasHydratedRef.current = false;
        runtime.thread.reset([]);
        return;
      }

      const sorted = [...res.value.messages].sort(
        (a, b) =>
          new Date(a.createdAt ?? 0).getTime() -
          new Date(b.createdAt ?? 0).getTime(),
      );

      // hydrate UI
      runtime.thread.reset(sorted.map(mapDbMessageToThreadMessage));

      /**
       * ----------------------------------------
       * STEP 1: Map completion index per toolCallId
       * ----------------------------------------
       */
      const completionIndexByToolCallId = new Map<string, number>();

      sorted.forEach((message, index) => {
        if (message.role === 'user') {
          for (const part of message.parts) {
            if (
              part.type === 'tool-result' &&
              part.name === 'custom_viz_result' &&
              part.toolCallId
            ) {
              completionIndexByToolCallId.set(part.toolCallId, index);
            }
          }
        }
      });

      /**
       * ----------------------------------------
       * STEP 2: Walk assistant tool results
       * ----------------------------------------
       */
      for (let i = 0; i < sorted.length; i++) {
        const message = sorted[i];
        if (message.role !== 'assistant') continue;

        for (const part of message.parts) {
          if (part.type !== 'tool-result') continue;
          if (part.name !== 'startCustomViz') continue;

          const parsed = CustomVizRequest.safeParse(part.result);
          if (!parsed.success) continue;

          const id = part.toolCallId;
          if (!id) continue;

          const completionIndex = completionIndexByToolCallId.get(id);

          /**
           * ----------------------------------------
           * CASE 1: Never completed → run
           * ----------------------------------------
           */
          if (completionIndex === undefined) {
            if (!executedVizRef.current.has(id)) {
              executedVizRef.current.add(id);

              setVizList((prev) => [
                ...prev,
                {
                  toolCallId: id,
                  request: parsed.data,
                },
              ]);
            }
            continue;
          }

          /**
           * ----------------------------------------
           * CASE 2: Completed but assistant never continued → resume
           * ----------------------------------------
           */
          const hasAssistantAfterCompletion = sorted
            .slice(completionIndex + 1)
            .some((m) => m.role === 'assistant');

          if (!hasAssistantAfterCompletion) {
            if (!executedVizRef.current.has(id)) {
              executedVizRef.current.add(id);

              setVizList((prev) => [
                ...prev,
                {
                  toolCallId: id,
                  request: parsed.data,
                },
              ]);
            }
            continue;
          }

          /**
           * ----------------------------------------
           * CASE 3: Fully completed → mark done
           * ----------------------------------------
           */
          vizCompletedRef.current.add(id);
        }
      }
    },
    [
      conversationId,
      databaseId,
      databaseRevisionId,
      organizationId,
      userAuthenticatedApiCall,
      runtime,
    ],
  );

  const onCustomVizResult = useCallback(
    (
      payload: {
        status: 'success' | 'error';
        result?: unknown;
        error?: unknown;
      },
      id: string,
    ) => {
      if (vizCompletedRef.current.has(id)) return;

      vizCompletedRef.current.add(id);

      runtime.thread.append(
        createCustomVizResultMessage({
          toolCallId: id,
          ...payload,
        }),
      );
    },
    [runtime],
  );

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {children}
      {vizList.map((viz) => (
        <CustomViz
          key={viz.toolCallId}
          observation={viz.request.observationLocationType}
          organizationId={viz.request.organizationId}
          customVizDefinition={lookupCustomVizDefinition(
            viz.request.customVizId,
          )}
          userAuthenticatedApiCall={userAuthenticatedApiCall}
        >
          <CustomVizChatContext
            customVizRequest={viz.request}
            toolCallId={viz.toolCallId}
            onResult={onCustomVizResult}
          />
        </CustomViz>
      ))}
    </AssistantRuntimeProvider>
  );
};
const CustomVizChatContext = (props: {
  customVizRequest: CustomVizRequest;
  onResult: (
    payload: {
      status: 'success' | 'error';
      result?: unknown;
      error?: unknown;
    },
    id: string,
  ) => void;
  toolCallId: string;
}) => {
  const context = useCustomViz();

  const didRunRef = useRef(false);
  const didCompleteRef = useRef(false);

  // kick off viz once
  useEffect(() => {
    if (didRunRef.current) return;
    didRunRef.current = true;
    context.doViz(props.customVizRequest);
  }, [context, props.customVizRequest]);

  const { isLoading, readyToDisplayDynamicContent, hasFailed } = useMemo(() => {
    return parseCustomVizResult({
      result: context.result,
      status: context.status,
      ResultZodType: z.object({}).passthrough(),
    });
  }, [context.result, context.status]);

  useEffect(() => {
    if (didCompleteRef.current) return;

    // don't do anything until it's actually ready
    if (isLoading || !readyToDisplayDynamicContent) return;

    // ✅ SUCCESS
    if (!hasFailed && isOk(context.result) && context.result.value) {
      didCompleteRef.current = true;

      props.onResult(
        {
          status: 'success',
          result: context.result.value,
        },
        props.toolCallId,
      );

      return;
    }

    // ❗ FAILURE
    if (hasFailed) {
      didCompleteRef.current = true;

      props.onResult(
        {
          status: 'error',
          error: isErr(context.result) ? context.result.error : undefined,
        },
        props.toolCallId,
      );
    }
  }, [
    context.result,
    hasFailed,
    isLoading,
    readyToDisplayDynamicContent,
    props,
  ]);

  return null;
};

export function AssistantProvider({ children }: { children: React.ReactNode }) {
  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();

  const { staleResultShowing, failedToLoadObservation, myObservation } =
    useObservation();

  const showChatbot = useMemo(
    () =>
      isShowChatbotEnabledForRevision({
        revisionFlags: myObservation,
      }) &&
      isEntitledToAi({
        features: myObservation?.features ?? [],
      }),
    [myObservation],
  );

  if (
    !myObservation ||
    staleResultShowing ||
    failedToLoadObservation ||
    !selectedOrganizationId ||
    !selectedDatabaseId ||
    !selectedDatabaseRevisionId ||
    !showChatbot
  ) {
    return <>{children}</>;
  }

  return (
    <AssistantProviderInner
      organizationId={selectedOrganizationId}
      databaseId={selectedDatabaseId}
      databaseRevisionId={selectedDatabaseRevisionId}
    >
      {children}
    </AssistantProviderInner>
  );
}
